/**
 * 镜头语言选择器(景别 / 运镜 / 镜速 / 光 / 承接)—— 受控枚举,**只选不写**。
 *
 * 词表 = `cameraLexicon`,与 ai 侧 `prompt_lexicon_zh` 逐字对齐(已核:景别 6 值 / 运镜 18 值零差异)。
 * 未选 = 「AI 定」,编译层跳过该子句 —— 所以按钮上写「AI 定」而不是留空:留空会让人以为控件坏了。
 *
 * ★2026-08-06 从 `PanelShotCard` 抽出:幕二重设计后这些选择器搬到右栏检视面板,
 *   两处引同一份实现,不复制第二套。
 */
import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";
import type { LexOption } from "@/shared/lib/video/cameraLexicon";

export function LexPicker({
  title,
  value,
  valueLabel,
  options,
  groups,
  onSelect,
  disabled,
  note,
}: {
  title: string;
  value?: string;
  valueLabel?: string;
  options?: readonly LexOption[];
  /** 分组渲染(运镜三档);与 options 二选一 */
  groups?: readonly { label: string; options: readonly LexOption[] }[];
  onSelect: (v: string | undefined) => void;
  disabled?: boolean;
  note?: string;
}) {
  const [open, setOpen] = useState(false);
  const pick = (v: string | undefined) => {
    onSelect(v);
    setOpen(false);
  };
  const renderOptions = (opts: readonly LexOption[]) => (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => pick(o.value)}
          className={cn(
            "cursor-pointer rounded-full border px-2.5 py-1 text-xs transition-colors",
            value === o.value
              ? "border-primary bg-primary font-semibold text-primary-foreground shadow-sm"
              : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition-colors",
            disabled
              ? "cursor-not-allowed border-border/40 text-muted-foreground/50"
              : value
                ? "border-primary/60 bg-primary/5 text-foreground hover:border-primary"
                : "border-border text-foreground hover:border-primary/50 hover:bg-primary/5"
          )}
        >
          <span className="text-muted-foreground">{title}</span>
          <span className={cn(value ? "font-semibold text-primary" : "font-medium")}>
            {valueLabel ?? "AI 定"}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2 p-3">
        <div className="text-xs font-medium">{title}</div>
        {groups
          ? groups.map((g) => (
              <div key={g.label} className="space-y-1">
                <div className="text-2xs text-muted-foreground">{g.label}</div>
                {renderOptions(g.options)}
              </div>
            ))
          : options && renderOptions(options)}
        {note && <p className="text-2xs leading-snug text-muted-foreground">{note}</p>}
        {value && (
          <button
            type="button"
            onClick={() => pick(undefined)}
            className="cursor-pointer text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          >
            清除,交回 AI 决定
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
